import { useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Toggle } from "@/components/ui/toggle";
import { ICON_CATEGORIES } from "components/icons/iconCategories";
import NamedIcon from "components/icons/NamedIcon";
import useLucideIcons from "components/icons/useLucideIcons";

// A name can sit in several categories; search and the footer count each once.
const ALL_ICON_NAMES = [
  ...new Set(ICON_CATEGORIES.flatMap(group => group.names)),
].sort();

// Matches the grid's tw:grid-cols-6.
const COLS = 6;
// First-render estimates of the rows' tw:h-8 and tw:h-10; the virtualizer
// measures the real heights from the rendered classes.
const HEADER_SIZE = 32;
const ROW_SIZE = 40;
// Match the scroll area's tw:h-60 and the popup's tw:w-80 less its padding, at
// the project's 4px spacing unit.
const GRID_HEIGHT = 240;
const GRID_WIDTH = 300;

const chunk = (names, size) => {
  const rows = [];
  for (let i = 0; i < names.length; i += size)
    rows.push(names.slice(i, i + size));
  return rows;
};

// Kept separate from IconPicker so the virtualizer is created and discarded
// with the popup. The instance caches the scroll element's measured rect and
// never clears it on unmount, so a picker-level instance would carry a stale
// (and, once the popup has closed, zero-height) rect into the next open, where
// a zero outer size makes it compute no range and mount no rows.
const IconPickerBrowser = ({ iconSet, value, onSelect }) => {
  const [query, setQuery] = useState("");
  const scrollRef = useRef(null);

  const searchTerm = query.trim().toLowerCase();
  const isSearching = searchTerm !== "";

  // Virtual rows are category headers or rows of COLS icon names. Searching
  // drops the headers, and `categoryIndex` maps a category to its header row.
  const { items, categoryIndex, iconCount } = useMemo(() => {
    if (searchTerm) {
      const matches = ALL_ICON_NAMES.filter(name => name.includes(searchTerm));
      const rows = chunk(matches, COLS).map((names, i) => ({
        type: "row",
        key: `r${i}`,
        names,
      }));
      return { items: rows, categoryIndex: {}, iconCount: matches.length };
    }
    const built = [];
    const index = {};
    for (const group of ICON_CATEGORIES) {
      index[group.key] = built.length;
      built.push({ type: "header", key: `h-${group.key}`, label: group.label });
      for (const [i, names] of chunk(group.names, COLS).entries()) {
        built.push({ type: "row", key: `r-${group.key}-${i}`, names });
      }
    }
    return {
      items: built,
      categoryIndex: index,
      iconCount: ALL_ICON_NAMES.length,
    };
  }, [searchTerm]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    // A new key function each render makes the virtualizer recompute sizes
    // when the grouped and search views swap.
    getItemKey: index => items[index].key,
    estimateSize: index =>
      items[index].type === "header" ? HEADER_SIZE : ROW_SIZE,
    overscan: 8,
    // Seeds the first render, before the scroll element's rect is observed. The
    // library's own default is a zero-height rect, on which it computes no range
    // and mounts no rows.
    initialRect: { width: GRID_WIDTH, height: GRID_HEIGHT },
  });

  const handleQueryChange = event => {
    setQuery(event.target.value);
    virtualizer.scrollToOffset(0);
  };

  const scrollToCategory = key => {
    virtualizer.scrollToIndex(categoryIndex[key], { align: "start" });
  };

  return (
    <>
      <InputGroup>
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          autoFocus
          placeholder="Search icons…"
          value={query}
          onChange={handleQueryChange}
          aria-label="Search icons"
        />
      </InputGroup>

      {!isSearching && (
        <div
          role="group"
          aria-label="Jump to category"
          className="tw:flex tw:gap-1 tw:overflow-x-auto tw:pb-0.5"
        >
          {ICON_CATEGORIES.map(group => (
            <Button
              key={group.key}
              type="button"
              variant="secondary"
              size="xs"
              className="tw:shrink-0"
              onClick={() => scrollToCategory(group.key)}
            >
              {group.label}
            </Button>
          ))}
        </div>
      )}

      <div
        ref={scrollRef}
        role="group"
        aria-label="Icon options"
        className="tw:h-60 tw:overflow-y-auto"
      >
        {items.length === 0 ? (
          <Empty className="tw:p-4">
            <EmptyDescription>
              No icons match &quot;{query}&quot;
            </EmptyDescription>
          </Empty>
        ) : (
          <div
            className="tw:relative tw:w-full"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualizer.getVirtualItems().map(virtualRow => {
              const item = items[virtualRow.index];
              const rowProps = {
                "data-index": virtualRow.index,
                ref: virtualizer.measureElement,
                style: { transform: `translateY(${virtualRow.start}px)` },
              };
              if (item.type === "header") {
                return (
                  <div
                    key={virtualRow.key}
                    {...rowProps}
                    className="tw:absolute tw:top-0 tw:left-0 tw:flex tw:h-8 tw:w-full tw:items-end tw:pb-1 tw:text-xs tw:font-semibold tw:text-muted-foreground"
                  >
                    {item.label}
                  </div>
                );
              }
              return (
                <div
                  key={virtualRow.key}
                  {...rowProps}
                  className="tw:absolute tw:top-0 tw:left-0 tw:grid tw:h-10 tw:w-full tw:grid-cols-6 tw:gap-1"
                >
                  {item.names.map(name => (
                    <Toggle
                      key={name}
                      size="lg"
                      pressed={name === value}
                      onPressedChange={() => onSelect(name)}
                      aria-label={`Icon ${name}`}
                      title={name}
                      className="tw:w-full tw:aria-pressed:text-primary"
                    >
                      <iconSet.NamedLucideIcon name={name} />
                    </Toggle>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="tw:px-1 tw:text-xs tw:text-muted-foreground">
        Icons: {iconCount}
      </div>
    </>
  );
};

// Gates the content on the icon set, so the picker's scroll element exists
// when its virtualizer is created.
const IconPickerPanel = ({ value, onSelect }) => {
  const { data: iconSet, isError } = useLucideIcons();

  if (isError) {
    return (
      <Empty className="tw:h-60">
        <EmptyDescription>Icons could not be loaded.</EmptyDescription>
      </Empty>
    );
  }
  if (!iconSet) {
    return (
      <div className="tw:flex tw:h-60 tw:items-center tw:justify-center">
        <Spinner />
      </div>
    );
  }
  return (
    <IconPickerBrowser iconSet={iconSet} value={value} onSelect={onSelect} />
  );
};

const IconPicker = ({ value, onChange, id, disabled = false }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = name => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              id={id}
              type="button"
              variant="outline"
              aria-label="Choose icon"
              disabled={disabled}
              className="tw:min-w-0 tw:justify-start"
            />
          }
        >
          {value ? (
            <>
              <NamedIcon name={value} className="tw:size-4" />
              <span className="tw:truncate tw:font-mono tw:text-xs tw:text-muted-foreground">
                {value}
              </span>
            </>
          ) : (
            <span className="tw:text-muted-foreground">Select an icon…</span>
          )}
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="tw:w-80">
          <IconPickerPanel value={value} onSelect={handleSelect} />
        </PopoverContent>
      </Popover>
      {value && (
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={() => onChange(null)}
          disabled={disabled}
        >
          Clear
        </Button>
      )}
    </div>
  );
};

export default IconPicker;
